<template>
  <div class="table-scroll">
    <v-data-table
      :headers="headers"
      :items="levels"
      item-value="code"
      :loading="loading"
      class="elevation-1"
      density="comfortable"
      :items-per-page="-1"
      hide-default-footer
    >
      <template #item.order="{ item, index }">
        <div class="order-actions">
          <template v-if="canReorder">
            <v-btn
              icon
              size="small"
              variant="text"
              :disabled="upDisabled(item, index)"
              :aria-label="$t('accessLevelLadderMoveUp')"
              @click="$emit('move', index, -1)"
            >
              <v-icon>mdi-chevron-up</v-icon>
              <v-tooltip activator="parent" location="top">{{ $t('accessLevelLadderMoveUp') }}</v-tooltip>
            </v-btn>
          </template>
          <span class="order-number">{{ index + 1 }}</span>
          <template v-if="canReorder">
            <v-btn
              icon
              size="small"
              variant="text"
              :disabled="downDisabled(item, index)"
              :aria-label="$t('accessLevelLadderMoveDown')"
              @click="$emit('move', index, 1)"
            >
              <v-icon>mdi-chevron-down</v-icon>
              <v-tooltip activator="parent" location="top">{{ $t('accessLevelLadderMoveDown') }}</v-tooltip>
            </v-btn>
          </template>
        </div>
      </template>

      <template #item.code="{ item }">
        <div class="d-flex align-center ga-2">
          <span class="font-weight-bold">{{ item.code }}</span>
          <v-chip v-if="item.isSystem" size="x-small" label variant="tonal" color="blue-grey" prepend-icon="mdi-lock-outline">
            {{ $t('accessLevelLadderLocked') }}
            <v-tooltip activator="parent" location="top">{{ $t('accessLevelLadderLockedTooltip') }}</v-tooltip>
          </v-chip>
          <v-chip v-else-if="isRequired(item)" size="x-small" label variant="tonal" color="indigo" prepend-icon="mdi-shield-check-outline">
            {{ $t('accessLevelLadderRequired') }}
            <v-tooltip activator="parent" location="top">{{ $t('accessLevelLadderRequiredTooltip') }}</v-tooltip>
          </v-chip>
          <v-chip v-if="movedCodes.includes(item.code)" size="x-small" label variant="tonal" color="warning">
            {{ $t('accessLevelLadderMoved') }}
          </v-chip>
        </div>
      </template>

      <template #item.nameTh="{ item }">
        <span class="cell-text">{{ item.nameTh || '-' }}</span>
      </template>
      <template #item.nameEn="{ item }">
        <span class="cell-text">{{ item.nameEn || '-' }}</span>
      </template>
      <template #item.description="{ item }">
        <span class="cell-text text-caption">{{ item.description || '-' }}</span>
      </template>

      <template #item.usage="{ item }">
        <span v-if="usageCounts === null" class="text-caption text-grey">{{ $t('accessLevelLadderUsageUnavailable') }}</span>
        <v-btn
          v-else-if="countOf(item.code) > 0"
          size="small"
          variant="tonal"
          color="primary"
          class="text-none"
          @click="$emit('show-usage', item)"
        >
          {{ countOf(item.code) }}
          <v-tooltip activator="parent" location="top">{{ $t('accessLevelLadderUsageShow') }}</v-tooltip>
        </v-btn>
        <span v-else class="text-grey">0</span>
      </template>

      <template #item.actions="{ item }">
        <div class="d-flex align-center justify-end">
          <v-btn
            v-if="canEdit"
            icon
            variant="text"
            density="comfortable"
            :disabled="editDisabled"
            :aria-label="$t('accessLevelLadderEdit')"
            @click="$emit('edit', item)"
          >
            <v-icon size="18">mdi-pencil-outline</v-icon>
            <v-tooltip activator="parent" location="top">{{ $t('accessLevelLadderEdit') }}</v-tooltip>
          </v-btn>
          <v-btn
            v-if="canDelete && !item.isSystem && !isRequired(item)"
            icon
            variant="text"
            density="comfortable"
            color="error"
            :disabled="deleteDisabled"
            :aria-label="$t('accessLevelLadderDelete')"
            @click="$emit('remove', item)"
          >
            <v-icon size="18">mdi-delete-outline</v-icon>
            <v-tooltip activator="parent" location="top">{{ $t('accessLevelLadderDelete') }}</v-tooltip>
          </v-btn>
        </div>
      </template>

      <template #no-data>
        <div class="py-6 text-center text-medium-emphasis">{{ $t('accessLevelLadderEmpty') }}</div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
/**
 * ตาราง A ของหน้า `/AccessLevelConfig` (#914) — แสดงขั้นบันไดตาม**ลำดับร่าง** (ต่ำ → สูง; แถวแรก T0, แถวท้าย T3)
 * ตารางนี้ไม่ยิง API เอง: ส่ง event ให้หน้าแม่ (`move` ลำดับร่าง, `edit`/`remove` เปิด dialog, `show-usage` ดูรายการ intent)
 * · T0/T3 (`isSystem`) ย้าย/ลบไม่ได้ และแถวอื่นย้ายข้ามสองระดับนี้ไม่ได้ (แถวที่ 2 ขึ้นไม่ได้ · แถวรองสุดท้ายลงไม่ได้)
 * · T1/T2 ลบไม่ได้ (ระบบใช้ — `requiredCodes`) แต่ย้ายได้
 */
export default {
  name: 'AccessLevelLadderTable',
  props: {
    /** ระดับเรียงตามลำดับร่าง `[{ id, code, nameTh, nameEn, description, isSystem }]` */
    levels: { type: Array, default: () => [] },
    /** code ที่ตำแหน่งในร่างต่างจากที่บันทึกไว้ (ติดป้าย "ย้ายแล้ว") */
    movedCodes: { type: Array, default: () => [] },
    /** code ที่โค้ดอ้างเป็น const (T0–T3) — ลบไม่ได้; T1/T2 ยังย้ายได้ */
    requiredCodes: { type: Array, default: () => [] },
    /** code (ตัวพิมพ์ใหญ่) → จำนวน intent ที่ตั้งระดับนั้น · null = นับไม่ได้ */
    usageCounts: { type: Object, default: null },
    canReorder: { type: Boolean, default: false },
    reorderDisabled: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: false },
    editDisabled: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
    deleteDisabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  emits: ['move', 'edit', 'remove', 'show-usage'],
  computed: {
    headers() {
      return [
        { title: this.$t('accessLevelLadderOrder'), key: 'order', width: '120px', sortable: false },
        { title: this.$t('accessLevelLadderCode'), key: 'code', minWidth: '150px', sortable: false },
        { title: this.$t('accessLevelLadderNameTh'), key: 'nameTh', minWidth: '200px', sortable: false },
        { title: this.$t('accessLevelLadderNameEn'), key: 'nameEn', minWidth: '200px', sortable: false },
        { title: this.$t('accessLevelLadderDescription'), key: 'description', minWidth: '240px', sortable: false },
        { title: this.$t('accessLevelLadderUsageCount'), key: 'usage', width: '120px', align: 'center', sortable: false },
        { title: '', key: 'actions', width: '110px', align: 'end', sortable: false },
      ];
    },
  },
  methods: {
    countOf(code) {
      if (!this.usageCounts) return 0;
      return this.usageCounts[String(code || '').toUpperCase()] || 0;
    },
    isRequired(item) {
      return !item.isSystem && this.requiredCodes.includes(String(item.code || '').toUpperCase());
    },
    upDisabled(item, index) {
      return this.reorderDisabled || !!item.isSystem || index <= 1;
    },
    downDisabled(item, index) {
      return this.reorderDisabled || !!item.isSystem || index >= this.levels.length - 2;
    },
  },
};
</script>

<style scoped>
.table-scroll {
  overflow-x: auto;
}

.order-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.order-actions .v-btn {
  min-width: 28px;
  width: 28px;
  height: 28px;
}

.order-number {
  min-width: 20px;
  text-align: center;
}

.cell-text {
  white-space: normal;
}
</style>
